import React from "react";

export type BadgeVariant = "success" | "warning" | "error" | "info" | "neutral" | "indigo";
export type BadgeSize = "sm" | "md";

export interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: React.ReactNode;
  icon?: React.ReactNode;
  dot?: boolean;
}

/**
 * La etiqueta del sistema.
 *
 * Vivía en `ui/` (la familia heredada de its-the-best) mientras existía
 * también un `primitives/Badge` con otra API. Se resolvió el duplicado:
 * este se queda —habla el lenguaje `ds-*` del sistema y es el más
 * completo (seis variantes, dos tamaños, punto e icono)— y el otro se
 * borró. Sube a `core/` porque `core/` es el design system canónico.
 *
 * De la API que se retiró (`default | success | warn | dark | outline`)
 * no se perdió nada en uso: solo la pintaba el escaparate. `warn` es
 * `warning`, `default` es `neutral`.
 */
/*
 * Píldoras SÓLIDAS (Anahí, 27-ago, sobre la referencia de tablas): el
 * color lleno con texto blanco — "Active" verde, "Progress" ámbar — en
 * vez del fondo suave con texto oscuro. Es EL badge del sistema, así
 * que el cambio llega a todas las pantallas de una vez.
 *
 * Verde/ámbar/rojo son semánticos (no de marca) y salen de la paleta
 * de Tailwind en su paso 500 — el mismo peso visual que la referencia;
 * violeta e índigo siguen siendo los de la casa.
 */
const variantClasses: Record<BadgeVariant, string> = {
  success: "bg-emerald-500 text-white",
  warning: "bg-amber-500 text-white",
  error: "bg-red-500 text-white",
  info: "bg-ds-violet-pop text-white",
  neutral: "bg-ds-ink-4 text-white",
  indigo: "bg-ds-purple text-white",
};

/** Sobre el color lleno, el punto es blanco a media luz. */
const dotColors: Record<BadgeVariant, string> = {
  success: "bg-white/80",
  warning: "bg-white/80",
  error: "bg-white/80",
  info: "bg-white/80",
  neutral: "bg-white/80",
  indigo: "bg-white/80",
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: "text-[11px] px-2.5 py-0.5 tracking-ds-label uppercase",
  md: "text-[11.5px] px-3 py-1 tracking-ds-label uppercase",
};

export const Badge: React.FC<BadgeProps> = ({
  variant = "neutral",
  size = "md",
  children,
  icon,
  dot = false,
}) => (
  <span
    className={[
      "inline-flex items-center gap-1.5 rounded-ds-pill font-semibold font-manrope",
      variantClasses[variant],
      sizeClasses[size],
    ].join(" ")}
  >
    {dot && (
      <span className={["h-1.5 w-1.5 rounded-full", dotColors[variant]].join(" ")} />
    )}
    {icon && <span className="shrink-0">{icon}</span>}
    {children}
  </span>
);
